import {
  BillingAddressElement,
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTestMode, stripe } from '../../../shared/api/stripe';
import { useFormat } from '../../../shared/i18n/format';
import { Button } from '../../../shared/ui/button';
import { ErrorState } from '../../../shared/ui/error-state';
import { Notice } from '../../../shared/ui/notice';
import { Skeleton } from '../../../shared/ui/skeleton';

/**
 * The embedded card form: Stripe's Payment Element and Billing Address Element driven by a
 * Checkout Session. Card data lives in Stripe's iframes only and never touches our DOM or our worker.
 * One of the two places in the web app that know Stripe's React names (the other stores a card in Organization
 * settings): they were renamed in the 2026-03-25 API version and may be again. `shared/api/stripe` loads Stripe.js.
 */

type Address = {
  name: string;
  line1: string;
  line2: string | null;
  city: string;
  region: string | null;
  postalCode: string;
  country: string;
};

export function PayOrderForm({
  clientSecret,
  billingAddress,
  cardSaveOffered,
  onPaid,
}: {
  clientSecret: string;
  /**
   * Prefill from the org billing profile, when the payer may read it. No email is prefilled: the session already has
   * the org's Stripe customer, whose email is the first billing email, and Stripe refuses a second one.
   */
  billingAddress: Address | null;
  /**
   * Whether the session offers to keep the card (the worker decides: only a payer who may manage the
   * organization's billing). Stripe renders its own consent checkbox; this adds our sentence beside it.
   */
  cardSaveOffered: boolean;
  /** The provider accepted the payment. The order is placed by the webhook, not by this page. */
  onPaid: () => void;
}) {
  return (
    <CheckoutElementsProvider
      stripe={stripe()}
      options={{
        clientSecret,
        defaultValues: {
          ...(billingAddress
            ? {
                billingAddress: {
                  name: billingAddress.name,
                  address: {
                    line1: billingAddress.line1,
                    line2: billingAddress.line2 ?? '',
                    city: billingAddress.city,
                    state: billingAddress.region ?? '',
                    postal_code: billingAddress.postalCode,
                    country: billingAddress.country,
                  },
                },
              }
            : {}),
        },
      }}
    >
      <Fields cardSaveOffered={cardSaveOffered} onPaid={onPaid} />
    </CheckoutElementsProvider>
  );
}

function Fields({ cardSaveOffered, onPaid }: { cardSaveOffered: boolean; onPaid: () => void }) {
  const { t } = useTranslation('order');
  const { formatCurrency } = useFormat();
  const state = useCheckoutElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.type === 'loading')
    return (
      <section aria-busy="true" aria-label={t('pay.starting')} className="grid gap-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </section>
    );
  if (state.type === 'error')
    return (
      <ErrorState size="compact" title={t('pay.startError')} description={state.error.message} />
    );

  const { checkout } = state;
  const currency = checkout.currency.toUpperCase();
  const taxReady = checkout.tax.status === 'ready';
  const total = checkout.total.total.minorUnitsAmount;

  async function pay() {
    setError(null);
    setPaying(true);
    try {
      // No `returnUrl` here: the Checkout Session already carries one, and Stripe refuses both.
      const result = await checkout.confirm({ redirect: 'if_required' });
      if (result.type === 'error') setError(result.error.message);
      else onPaid();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('pay.startError'));
    } finally {
      setPaying(false);
    }
  }

  return (
    // Not a <form>: Stripe's Payment Element hooks the enclosing form's submit event and stops it, so a
    // type="submit" button would never reach React's handler. The payment is confirmed by the button itself.
    <div className="grid gap-6">
      {isTestMode() ? <Notice>{t('pay.testMode')}</Notice> : null}
      <fieldset className="grid gap-2">
        <legend className="mb-1 font-bold text-heading">{t('pay.billingAddress')}</legend>
        <p className="text-sm text-muted-foreground">{t('pay.billingAddressHelp')}</p>
        <BillingAddressElement />
      </fieldset>
      <fieldset className="grid gap-2">
        <legend className="mb-1 font-bold text-heading">{t('pay.card')}</legend>
        <PaymentElement options={{ layout: 'tabs' }} />
        {cardSaveOffered ? (
          <p className="text-sm text-muted-foreground">{t('pay.saveCardNote')}</p>
        ) : null}
      </fieldset>
      <dl className="grid gap-2 border-t pt-4" aria-live="polite">
        <div className="flex justify-between gap-4">
          <dt>{t('pay.subtotal')}</dt>
          <dd>{formatCurrency(checkout.total.subtotal.minorUnitsAmount, currency)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>{t('pay.tax')}</dt>
          <dd className={taxReady ? undefined : 'text-sm text-muted-foreground'}>
            {taxReady
              ? formatCurrency(checkout.total.taxExclusive.minorUnitsAmount, currency)
              : t('pay.taxPending')}
          </dd>
        </div>
        <div className="flex justify-between gap-4 font-bold text-heading">
          <dt>{t('pay.total')}</dt>
          <dd>{formatCurrency(total, currency)}</dd>
        </div>
      </dl>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div>
        <Button
          type="button"
          variant="accent"
          size="lg"
          disabled={!checkout.canConfirm || paying}
          onClick={() => void pay()}
        >
          {paying
            ? t('pay.paying')
            : taxReady
              ? t('pay.payNow', { amount: formatCurrency(total, currency) })
              : t('pay.payNowPlain')}
        </Button>
      </div>
    </div>
  );
}
